import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

export const SlopeChart: React.FC<ChartProps> = ({ metrics, chartId = 'slope' }) => {
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getPerformanceColors = (brandValue: number, industryMean: number) => {
    const absoluteDifference = calculateAbsoluteDifference(brandValue, industryMean);
    const perfLevel = getPerformanceLevel(absoluteDifference);

    const colorMaps = {
      'excellent': { color: '#16a34a', bg: '#dcfce7', performance: 'excellent' as const },
      'very-good': { color: '#22c55e', bg: '#ecfccb', performance: 'very-good' as const },
      'good': { color: '#4ade80', bg: '#f0fdf4', performance: 'good' as const },
      'fair': { color: '#f59e0b', bg: '#fef3c7', performance: 'fair' as const },
      'poor': { color: '#f97316', bg: '#fed7aa', performance: 'poor' as const },
      'critical': { color: '#dc2626', bg: '#fecaca', performance: 'critical' as const }
    };

    return colorMaps[perfLevel.performance];
  };

  useEffect(() => {
    if (!chartRef.current || !containerRef.current || metrics.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(chartRef.current);
    svg.selectAll('*').remove();

    const containerRect = container.getBoundingClientRect();
    const margin = { top: 40, right: 120, bottom: 60, left: 120 };
    const width = containerRect.width - margin.left - margin.right;
    const height = Math.max(500, metrics.length * 30);

    svg.attr('width', containerRect.width).attr('height', height + margin.top + margin.bottom);

    // Create tooltip
    const tooltip = d3.select('body').selectAll(`.slope-tooltip-${chartId}`)
      .data([null])
      .join('div')
      .attr('class', `slope-tooltip slope-tooltip-${chartId}`)
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('z-index', '1000')
      .style('pointer-events', 'none');

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Filter metrics with industry data
    const validMetrics = metrics.filter(d => d.industryMean && d.industryMean > 0);

    if (validMetrics.length === 0) {
      g.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', '#6b7280')
        .text('No industry benchmark data available');
      return;
    }

    // Calculate scales
    const allValues = validMetrics.flatMap(d => [d.value, d.industryMean || 0]);
    const minValue = d3.min(allValues) || 0;
    const maxValue = d3.max(allValues) || 100;
    const valueRange = maxValue - minValue;
    const padding = valueRange * 0.1;

    const yScale = d3.scaleLinear()
      .domain([minValue - padding, maxValue + padding])
      .range([height, 0]);

    const xScale = d3.scalePoint()
      .domain(['Industry Average', 'Brand Performance'])
      .range([0, width])
      .padding(0.2);

    const positionScale = d3.scaleBand()
      .domain(validMetrics.map(d => d.categoryName))
      .range([0, height])
      .padding(0.1);

    // Add column headers
    g.append('text')
      .attr('x', xScale('Industry Average') || 0)
      .attr('y', -15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', '600')
      .attr('fill', '#94a3b8')
      .text('Industry Average');

    g.append('text')
      .attr('x', xScale('Brand Performance') || 0)
      .attr('y', -15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', '600')
      .attr('fill', '#374151')
      .text('Brand Performance');

    // Add vertical reference lines
    g.append('line')
      .attr('x1', xScale('Industry Average') || 0)
      .attr('x2', xScale('Industry Average') || 0)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');

    g.append('line')
      .attr('x1', xScale('Brand Performance') || 0)
      .attr('x2', xScale('Brand Performance') || 0)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');

    // Calculate initial label positions and detect collisions
    interface LabelPosition {
      metric: typeof validMetrics[0];
      index: number;
      originalY: number;
      adjustedY: number;
      industryY: number;
      brandY: number;
      colors: ReturnType<typeof getPerformanceColors>;
    }

    const labelPositions: LabelPosition[] = validMetrics.map((d, i) => {
      const colors = getPerformanceColors(d.value, d.industryMean || 0);
      const industryY = yScale(d.industryMean || 0);
      const brandY = yScale(d.value);
      const midY = (industryY + brandY) / 2;

      return {
        metric: d,
        index: i,
        originalY: midY,
        adjustedY: midY,
        industryY,
        brandY,
        colors
      };
    });

    // Sort by Y position for collision detection
    labelPositions.sort((a, b) => a.adjustedY - b.adjustedY);

    // Adjust overlapping labels - simple greedy algorithm
    const minLabelSpacing = 16; // Minimum pixels between labels
    for (let i = 1; i < labelPositions.length; i++) {
      const prev = labelPositions[i - 1];
      const curr = labelPositions[i];

      if (curr.adjustedY - prev.adjustedY < minLabelSpacing) {
        curr.adjustedY = prev.adjustedY + minLabelSpacing;
      }
    }

    // Second pass: if labels got pushed too far down, push them back up
    for (let i = labelPositions.length - 2; i >= 0; i--) {
      const curr = labelPositions[i];
      const next = labelPositions[i + 1];

      if (next.adjustedY - curr.adjustedY < minLabelSpacing) {
        curr.adjustedY = next.adjustedY - minLabelSpacing;
      }
    }

    // Restore original order
    labelPositions.sort((a, b) => a.index - b.index);

    // Draw slope lines
    labelPositions.forEach(({ metric: d, adjustedY: labelY, industryY, brandY, colors }, i) => {
      const isImproving = d.value > (d.industryMean || 0);

      // Draw connecting line
      g.append('line')
        .attr('x1', xScale('Industry Average') || 0)
        .attr('y1', industryY)
        .attr('x2', xScale('Brand Performance') || 0)
        .attr('y2', brandY)
        .attr('stroke', colors.color)
        .attr('stroke-width', 2)
        .attr('opacity', 0.7)
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          const absoluteDiff = calculateAbsoluteDifference(d.value, d.industryMean || 0);
          const performanceText = colors.performance === 'excellent' ? 'Outstanding Performance' :
                                 colors.performance === 'very-good' ? 'Strong Performance' :
                                 colors.performance === 'good' ? 'Above Average' :
                                 colors.performance === 'fair' ? 'Below Average' :
                                 colors.performance === 'poor' ? 'Needs Improvement' : 'Urgent Attention Required';

          tooltip.html(`
            <div style="
              background: ${colors.bg};
              border: 2px solid ${colors.color};
              border-radius: 12px;
              padding: 16px;
              min-width: 200px;
              box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.25);
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            ">
              <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 8px;">
                ${d.categoryName}
              </div>
              <div style="color: #475569; margin-bottom: 8px; font-size: 12px;">
                ${performanceText}
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Gap:</span>
                <span style="font-weight: 600; color: ${isImproving ? colors.color : '#dc2626'}; font-size: 12px;">
                  ${isImproving ? '+' : ''}${(d.value - (d.industryMean || 0)).toFixed(1)} pts
                </span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Brand Score:</span>
                <span style="font-weight: 600; color: #1e293b; font-size: 12px;">${d.value.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #64748b; font-size: 12px;">Industry Avg:</span>
                <span style="font-weight: 600; color: #64748b; font-size: 12px;">${d.industryMean?.toFixed(1)}%</span>
              </div>
            </div>
          `)
          .style('visibility', 'visible');

          d3.select(this).attr('stroke-width', 4).attr('opacity', 1);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');
          d3.select(this).attr('stroke-width', 2).attr('opacity', 0.7);
        });

      // Draw industry average point
      g.append('circle')
        .attr('cx', xScale('Industry Average') || 0)
        .attr('cy', industryY)
        .attr('r', 4)
        .attr('fill', '#94a3b8')
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      // Draw brand performance point
      g.append('circle')
        .attr('cx', xScale('Brand Performance') || 0)
        .attr('cy', brandY)
        .attr('r', 5)
        .attr('fill', colors.color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      // Add metric labels on the left (using adjusted Y position to avoid overlap)
      const categoryLabel = g.append('text')
        .attr('x', -10)
        .attr('y', labelY)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '500')
        .attr('fill', '#374151')
        .text(d.categoryName.length > 20 ? d.categoryName.substring(0, 20) + '...' : d.categoryName);

      // Add tooltip for truncated labels
      if (d.categoryName.length > 20) {
        categoryLabel.append('title').text(d.categoryName);
      }

      // Add value labels
      g.append('text')
        .attr('x', (xScale('Industry Average') || 0) - 8)
        .attr('y', industryY)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', '600')
        .attr('fill', '#94a3b8')
        .text(`${(d.industryMean || 0).toFixed(1)}%`);

      g.append('text')
        .attr('x', (xScale('Brand Performance') || 0) + 8)
        .attr('y', brandY)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', '600')
        .attr('fill', colors.color)
        .text(`${d.value.toFixed(1)}%`);
    });

    // Add legend
    const legend = g.append('g')
      .attr('transform', `translate(${width + 20}, 20)`);

    legend.append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#374151')
      .text('Performance vs Industry');

    legend.append('line')
      .attr('x1', 0)
      .attr('y1', 20)
      .attr('x2', 30)
      .attr('y2', 10)
      .attr('stroke', '#16a34a')
      .attr('stroke-width', 2);

    legend.append('text')
      .attr('x', 35)
      .attr('y', 15)
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .text('Above average');

    legend.append('line')
      .attr('x1', 0)
      .attr('y1', 50)
      .attr('x2', 30)
      .attr('y2', 60)
      .attr('stroke', '#dc2626')
      .attr('stroke-width', 2);

    legend.append('text')
      .attr('x', 35)
      .attr('y', 55)
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .text('Below average');

    // Cleanup function
    return () => {
      d3.select('body').select(`.slope-tooltip-${chartId}`).remove();
    };
  }, [metrics, chartId]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};